import { api } from './client';
import type { PAStatus } from './pa';

/**
 * ============================================================================
 * THE TARIFF-CHANGE CLIENT — §7 / Appendix L §2.12, F16, T2(a)
 * ============================================================================
 *
 *   GET    /pa/tariff/{id}?contract_id=                — §7a/7b/7d
 *   POST   /pa/tariff/{id}/schedule?contract_id=       — §7c: «Сменить»
 *   DELETE /pa/tariff/{id}/schedule/{aid}?contract_id= — §7d: «Удалить из расписания»
 *
 * It mirrors `backend/internal/personalarea/tariffchange_handlers.go`, pinned BOTH WAYS to
 * `api/openapi.yaml` by `src/api/wire-spec.test.ts`.
 *
 * ============================================================================
 * ⚠ THE ONE THING THIS SURFACE EXISTS TO GET RIGHT: **THE DATE**
 * ============================================================================
 *
 * T2(a) says a scheduled change fires at the next billing-cycle boundary at or after its
 * chosen date. The engine honours that for MONEY (`billing.resolveTariffID` picks the tariff
 * at each cycle open) and NOT for DISPLAY (the assignment flips to `active` at its own date,
 * so «ваш тариф» would change before the charge does). Rather than document that divergence
 * the server removes it: the assignment is written AT the boundary, and any date the
 * Subscriber picks inside the window is **SNAPPED FORWARD** to it.
 *
 * **So the screen prints the date the SERVER wrote and never the date the subscriber typed.**
 * `POST …/schedule` answers with `effective_date`, and that echo is the whole point of the
 * field: a Subscriber told his tariff changes on a date when his charge does not is exactly
 * the complaint this item exists to prevent. The same rule governs the read —
 * `PAScheduledTariff.effective_date` and not `effective_at`, because a civil day sent as an
 * instant is formatted by the browser in ITS OWN zone and renders as the previous day for
 * any reader west of a +03 operator.
 *
 * ============================================================================
 * ⚠ AND TWO SERVER VERDICTS THIS CLIENT NEVER RE-DERIVES
 * ============================================================================
 *
 * `boundary_known` — whether the charging engine could name the next moment money moves for
 * this Account at all. FALSE means no change can be scheduled and `available` is empty; the
 * honest screen explains, because naming a date the charge does not honour is the defect.
 *
 * `change_available` — «may the form be offered»: a boundary is known AND the operator has
 * flagged at least one transition. It is on the wire for the reason `can_block` is: the rule
 * is the server's, and a client copy (`available.length > 0`) is a second statement that can
 * drift from the one that enforces.
 */

/** `PATariffOption` — one tariff: his own, or one he may switch to. */
export interface PATariffOption {
  tariff_id: number;
  name: string;
  description: string;
  /** "" | "A" | "B" | "C" — empty when the tariff sets none. The portal owns the words. */
  billing_model: string;
  /**
   * MINOR UNITS, and it is the price effective at the date the row was resolved for — «now»
   * for the current tariff, **THE BOUNDARY** for an offered one. So the figure a Subscriber
   * compares is the figure his first cycle on it will be charged, not today's.
   *
   * ⚠ IT CARRIES NO RECURRING-METHOD DISCOUNT. §3.11's reduction is applied inside the
   * charging engine at `blockAwareFeePrice`, the one fee funnel, and a screen must never
   * subtract a percentage from this — `money-arithmetic.test.ts` makes the attempt fail.
   */
  subscription_fee: number | null;
  bandwidth_kbps: number | null;
}

/** `PAScheduledTariff` — one pending primary-tariff change (§7d). */
export interface PAScheduledTariff {
  /** What the DELETE names. */
  assignment_id: number;
  tariff: PATariffOption;
  /**
   * The civil day the change takes effect, YYYY-MM-DD in the OPERATOR's zone — **the one
   * date a screen should print.** See the file header for why the instant beside it is not.
   */
  effective_date: string;
  /** The same moment as an instant, for a client that needs one. Nothing here prints it. */
  effective_at: string;
  /**
   * ⚠ TRUE MEANS THE OPERATOR PLANNED IT, AND IT IS NOT COSMETIC. Only a subscriber-planned
   * change is guaranteed to sit on a cycle boundary — this surface writes nothing else —
   * while a staff-planned one may carry any date, and then the day the screen changes and the
   * day the money changes genuinely differ. A screen that wants to warn about that has this
   * flag and nothing else to go on.
   */
  scheduled_by_staff: boolean;
}

/** `PATariff` — the whole «Смена тарифного плана» screen for one Account. */
export interface PATariff {
  account_id: number;
  login: string;
  /** The primary tariff in force; null when the Account has never been given one. */
  current: PATariffOption | null;
  scheduled: PAScheduledTariff[];
  /** The operator's allowed-transition list, priced AT THE BOUNDARY. Empty is ordinary. */
  available: PATariffOption[];
  /** See the file header: FALSE ⇒ no form, and an explanation instead of a guess. */
  boundary_known: boolean;
  /** The day a change requested now takes effect — BOTH the display and the money day. */
  effective_date: string;
  /** The first day he may PICK (tomorrow). "" when the boundary is unknown. */
  earliest_date: string;
  /** The boundary itself — the last day he may pick. Only ONE boundary is knowable. */
  latest_date: string;
  /** THE SERVER'S OWN VERDICT on whether «Сменить» may be offered. Never re-derived. */
  change_available: boolean;
  /**
   * The OPERATOR's scheduled removal of this tariff (fixes8 T-10, customer 2026-09-24: «PA
   * change refused while pending»), null when none. While it is set `change_available` is
   * false and the screen prints the date instead of the form. There is no cancel here —
   * only staff cancel a removal.
   */
  pending_removal: PATariffRemoval | null;
}

/** `PATariffRemoval` — a pending tariff removal. Print `effective_date`, never the instant. */
export interface PATariffRemoval {
  effective_date: string;
  effective_at: string;
}

/** `PATariffChangeRequest` — the body of §7c's «Сменить». */
export interface PATariffChangeRequest {
  tariff_id: number;
  /**
   * OPTIONAL civil date. Empty means «at the next boundary», which is also what any date
   * inside `[earliest_date, latest_date]` resolves to — the server SNAPS IT FORWARD and the
   * response says which date was written. Anything outside is refused with
   * `pa.tariff_date_out_of_window`, whose `details` carry both ends.
   */
  from?: string;
}

// ---- calls ------------------------------------------------------------------

/** getTariff reads §7a, §7b and §7d for one Account of one contract in ONE request. */
export function getTariff(contractID: number, accountID: number): Promise<PATariff> {
  return api.get<PATariff>(`/pa/tariff/${accountID}?contract_id=${contractID}`);
}

/**
 * scheduleTariffChange books the change and answers with THE DATE THAT WAS WRITTEN.
 *
 * `from` is sent only when the subscriber typed one: an empty string means «at the next
 * boundary» on the server, and a client that substituted a browser-derived date for it would
 * be deciding the operator's civil day for him — the browser knows neither
 * `general.billing_timezone` nor the Account's cadence.
 *
 * ⚠ THE ANSWER'S `tariff` CARRIES ONLY ITS ID. The write's response is what HAPPENED, not a
 * re-read: the server fills `assignment_id`, `effective_date` and `effective_at` and leaves
 * the name, the fee and the description empty. So a screen must take the DATE from here and
 * re-read the list for everything else — rendering `out.tariff.name` would print a blank
 * where the tariff he chose should be.
 */
export function scheduleTariffChange(
  contractID: number,
  accountID: number,
  tariffID: number,
  from: string,
): Promise<PAScheduledTariff> {
  const body: PATariffChangeRequest = { tariff_id: tariffID };
  if (from !== '') body.from = from;
  return api.post<PAScheduledTariff>(
    `/pa/tariff/${accountID}/schedule?contract_id=${contractID}`,
    body,
  );
}

/**
 * cancelScheduledTariff is §7d's «Удалить из расписания».
 *
 * ⚠ IT IS THE ROUTE THE SPECIFICATION REQUIRED IN FOUR PLACES AND NOTHING IMPLEMENTED until
 * 2026-08-12 — while the neighbouring scheduled STATUS change has had a cancel since Phase 1
 * («one door of a pair hardened, its neighbour not», this project's most repeated finding).
 * It cancels the row AND its timer in one transaction.
 *
 * There is no EDIT beside it, and that is not an omission: an edit is a cancel plus a
 * schedule, which these two routes compose with no half-applied state in between.
 */
export function cancelScheduledTariff(
  contractID: number,
  accountID: number,
  assignmentID: number,
): Promise<PAStatus> {
  return api.del<PAStatus>(
    `/pa/tariff/${accountID}/schedule/${assignmentID}?contract_id=${contractID}`,
  );
}
